import { useState } from 'react';
import { Alert, App, Button, Input, InputNumber, Modal, Segmented, Select, Space, Switch, Tabs, Tag } from 'antd';
import { api } from '../api';
import { DataTable, ErrorNotice, Panel } from '../components';
import { formatDate, formatDateTime, useLoad } from '../lib';

/**
 * Parts of an account shared by ADMIN › Accounts (in drawers) and the account's profile (in tabs): its Kivonex Wallet,
 * its dealer credits, and the Activation / Renewal ADMIN gives it without a payment.
 */
export const money = (paise) => `₹${(Number(paise || 0) / 100).toFixed(2)}`;

/** The account's Kivonex Wallet: balance, manual adjustments with a reason, transactions and API usage. */
export function WalletPanel({ account, onChanged }) {
    const { message } = App.useApp();
    const loaded = useLoad(() => account ? api(`/v1/admin/accounts/${account.id}/wallet`) : Promise.resolve(null), [account?.id]);
    const [amount, setAmount] = useState(0);
    const [reason, setReason] = useState('');
    const [detail, setDetail] = useState(null);
    async function adjust(direction) {
        try {
            await api(`/v1/admin/accounts/${account.id}/wallet/adjustments`, { method: 'POST', body: JSON.stringify({ direction, amountPaise: Math.round(amount * 100), reason }) });
            message.success(`Wallet ${direction} recorded`); setAmount(0); setReason(''); await loaded.reload(); await onChanged?.();
        } catch (error) { message.error(error instanceof Error ? error.message : 'Could not adjust wallet'); }
    }
    async function inspect(row) {
        try { setDetail((await api(`/v1/admin/accounts/${account.id}/api-calls/${row.id}`)).call); }
        catch (error) { message.error(error instanceof Error ? error.message : 'Could not load response'); }
    }
    async function resolve(row, outcome) {
        const note = window.prompt(`Reason for marking this usage ${outcome}?`);
        if (!note?.trim()) return;
        try { await api(`/v1/admin/accounts/${account.id}/api-calls/${row.id}/resolve`, { method: 'POST', body: JSON.stringify({ outcome, reason: note }) }); await loaded.reload(); await onChanged?.(); }
        catch (error) { message.error(error instanceof Error ? error.message : 'Could not reconcile usage'); }
    }
    const data = loaded.data;
    return <>
      <ErrorNotice error={loaded.error}/>
      {data && <>
        <Space wrap size="large"><strong>Total {money(data.wallet.balancePaise)}</strong><span>Reserved {money(data.wallet.reservedPaise)}</span><Tag color={data.wallet.availablePaise > 0 ? 'green' : 'default'}>Available {money(data.wallet.availablePaise)}</Tag></Space>
        <Panel title="Manual adjustment" description="Ledger rows are immutable. Every adjustment needs a reason.">
          <Space wrap><InputNumber placeholder="500" min={0.01} precision={2} prefix="₹" value={amount} onChange={(value) => setAmount(Number(value || 0))}/><Input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Required reason" style={{ width: 320 }}/><Button type="primary" disabled={!amount || reason.trim().length < 3} onClick={() => adjust('credit')}>Credit</Button><Button danger disabled={!amount || reason.trim().length < 3 || amount * 100 > data.wallet.availablePaise} onClick={() => adjust('debit')}>Debit</Button></Space>
        </Panel>
        <Tabs items={[
          { key: 'ledger', label: 'Transactions', children: <DataTable rowKey="id" dataSource={data.transactions ?? []} pagination={{ pageSize: 10 }} columns={[{ title: 'When', render: (_v,row) => formatDateTime(row.createdAt) }, { title: 'Type', dataIndex: 'type' }, { title: 'Direction', render: (_v,row) => <Tag color={row.direction === 'credit' ? 'green' : 'red'}>{row.direction}</Tag> }, { title: 'Amount', render: (_v,row) => money(row.amountPaise) }, { title: 'Balance after', render: (_v,row) => money(row.balanceAfterPaise) }, { title: 'Reason', dataIndex: 'note' }]}/> },
          { key: 'usage', label: 'Usage', children: <DataTable rowKey="id" dataSource={data.calls ?? []} pagination={{ pageSize: 10 }} onRow={(row) => ({ onClick: () => inspect(row), style: { cursor: 'pointer' } })} columns={[{ title: 'When', render: (_v,row) => formatDateTime(row.createdAt) }, { title: 'Operation', dataIndex: 'operation' }, { title: 'Status', render: (_v,row) => <Tag color={row.status === 'charged' ? 'green' : row.status === 'failed' ? 'red' : 'orange'}>{row.pricePaise === 0 && row.status === 'charged' ? 'free' : row.status}</Tag> }, { title: 'Fee', render: (_v,row) => money(row.pricePaise) }, { title: 'Request ID', dataIndex: 'providerRequestId' }, { title: '', render: (_v,row) => row.status === 'uncertain' && <Space><Button size="small" onClick={(event) => { event.stopPropagation(); void resolve(row, 'success'); }}>Charge</Button><Button size="small" danger onClick={(event) => { event.stopPropagation(); void resolve(row, 'failed'); }}>Release</Button></Space> }]}/> }
        ]}/>
      </>}
      <Modal open={Boolean(detail)} title="Stored service response" footer={null} onCancel={() => setDetail(null)} width={780} destroyOnHidden><pre style={{ maxHeight: 520, overflow: 'auto', padding: 14, background: '#0f172a', color: '#e2e8f0' }}>{JSON.stringify(detail?.response ?? null, null, 2)}</pre></Modal>
    </>;
}

const CREDIT_LABEL = { activation: 'Activation', renewal: 'Renewal' };
const REASON_LABEL = { credit: 'Credited', debit: 'Debited', activation: 'Activation given', renewal: 'Renewal given' };

/**
 * Dealer settings of an account: whether it is a dealer, and its Activation and Renewal credits (kept apart). Credits
 * are given here by hand after a deal made outside the system; each activation or renewal the dealer gives debits them.
 */
export function DealerPanel({ account, onChanged }) {
    const { message } = App.useApp();
    const loaded = useLoad(() => account ? api(`/v1/admin/dealers/${account.id}`) : Promise.resolve(null), [account?.id]);
    const [kind, setKind] = useState('activation');
    const [units, setUnits] = useState(null);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const data = loaded.data;
    async function setDealer(dealer) {
        setBusy(true);
        try {
            await api(`/v1/admin/dealers/${account.id}`, { method: 'PUT', body: JSON.stringify({ dealer }) });
            message.success(dealer ? `${account.name} is a dealer` : `${account.name} is no longer a dealer`);
            await loaded.reload();
            await onChanged?.();
        }
        catch (error) { message.error(error instanceof Error ? error.message : 'Could not change the dealer setting'); }
        finally { setBusy(false); }
    }
    async function adjust(direction) {
        setBusy(true);
        try {
            const count = Number(units || 0);
            await api(`/v1/admin/dealers/${account.id}/credits`, { method: 'POST', body: JSON.stringify({ kind, units: direction === 'credit' ? count : -count, note }) });
            message.success(`${count} ${CREDIT_LABEL[kind]} ${count === 1 ? 'credit' : 'credits'} ${direction === 'credit' ? 'added' : 'taken away'}`);
            setUnits(null);
            setNote('');
            await loaded.reload();
            await onChanged?.();
        }
        catch (error) { message.error(error instanceof Error ? error.message : 'Could not change the credits'); }
        finally { setBusy(false); }
    }
    const balance = data?.balances?.[kind] ?? 0;
    return <>
      <ErrorNotice error={loaded.error}/>
      {data && <>
        <div className="switch-row">
          <div>
            <strong>Dealer</strong>
            <span>A dealer sees the Dealer pages in the account panel and can activate and renew other accounts with its credits.</span>
          </div>
          <Switch aria-label="Dealer" checked={data.dealer} loading={busy} onChange={setDealer}/>
        </div>
        {data.dealer ? <>
          <Space size="large" wrap style={{ margin: '18px 0' }}>
            <Tag color="green" style={{ padding: '6px 12px', fontSize: 14 }}>Activation credits: <strong>{data.balances.activation}</strong></Tag>
            <Tag color="blue" style={{ padding: '6px 12px', fontSize: 14 }}>Renewal credits: <strong>{data.balances.renewal}</strong></Tag>
          </Space>
          <Panel title="Credit or debit" description="One Activation credit activates one new account for life. One Renewal credit renews an activated account for one year. Every change is kept in the history below.">
            <Space wrap>
              <Segmented value={kind} onChange={setKind} options={[{ value: 'activation', label: 'Activation' }, { value: 'renewal', label: 'Renewal' }]}/>
              <InputNumber placeholder="10" min={1} max={10000} precision={0} value={units} onChange={setUnits} suffix="credits" style={{ width: 150 }}/>
              <Input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Note, e.g. deal or receipt number" maxLength={300} style={{ width: 280 }}/>
              <Button type="primary" disabled={!units || busy} onClick={() => adjust('credit')}>Credit</Button>
              <Button danger disabled={!units || busy || Number(units) > balance} onClick={() => adjust('debit')}>Debit</Button>
            </Space>
          </Panel>
          <DataTable rowKey="id" dataSource={data.entries} pagination={data.entries.length > 10 ? { pageSize: 10 } : false} locale={{ emptyText: 'No credits given yet.' }} columns={[
            { title: 'When', width: 170, render: (_v, row) => formatDateTime(row.createdAt) },
            { title: 'Credits', width: 110, render: (_v, row) => <Tag color={row.kind === 'renewal' ? 'blue' : 'green'}>{CREDIT_LABEL[row.kind]}</Tag> },
            { title: 'What', width: 150, render: (_v, row) => REASON_LABEL[row.reason] ?? row.reason },
            { title: 'Change', width: 90, align: 'right', render: (_v, row) => <span className="num" style={{ color: row.units > 0 ? '#087a3e' : '#b42318' }}>{row.units > 0 ? `+${row.units}` : row.units}</span> },
            { title: 'Left', width: 70, align: 'right', render: (_v, row) => <span className="num">{row.balanceAfter}</span> },
            { title: 'Account / note', render: (_v, row) => row.target ? <span>{row.target.name}<span className="cell-sub">{row.target.mobile}{row.years ? ` · ${row.years} ${row.years === 1 ? 'year' : 'years'}` : ''}</span></span> : (row.note || <span className="cell-sub">—</span>) },
            { title: 'By', width: 140, dataIndex: 'actor' }
          ]}/>
        </> : <Alert type="info" showIcon style={{ marginTop: 18 }} title="Turn on Dealer to give this account Activation and Renewal credits."/>}
      </>}
    </>;
}

/**
 * Activation and Renewal given by ADMIN, free (no payment, no dealer credits). `activate(account)` asks to confirm;
 * `renew(account)` opens a dialog to choose the years. Render `dialog` once in the page.
 */
export function useGrants(onDone) {
    const { message, modal } = App.useApp();
    const [renewing, setRenewing] = useState(null);
    const [years, setYears] = useState(1);
    const [busy, setBusy] = useState(false);
    function activate(account) {
        modal.confirm({
            title: `Activate ${account.name}?`,
            content: 'Unlocks Kivonex on this account for life, with the Activation plan\'s included months of renewal from today. No payment is taken.',
            okText: 'Activate',
            onOk: async () => {
                try {
                    const result = await api(`/v1/admin/accounts/${account.id}/activate`, { method: 'POST' });
                    message.success(`${account.name} is activated, with renewal till ${formatDate(result.subscription.validUntil)}`);
                    await onDone?.();
                }
                catch (error) {
                    message.error(error instanceof Error ? error.message : 'Could not activate the account');
                }
            }
        });
    }
    function renew(account) {
        setYears(1);
        setRenewing(account);
    }
    async function confirmRenew() {
        setBusy(true);
        try {
            const result = await api(`/v1/admin/accounts/${renewing.id}/renew`, { method: 'POST', body: JSON.stringify({ years }) });
            message.success(`${renewing.name} is renewed till ${formatDate(result.subscription.validUntil)}`);
            setRenewing(null);
            await onDone?.();
        }
        catch (error) {
            message.error(error instanceof Error ? error.message : 'Could not renew the account');
        }
        finally {
            setBusy(false);
        }
    }
    const dialog = (<Modal title={`Renew ${renewing?.name ?? ''}`} open={Boolean(renewing)} onCancel={() => setRenewing(null)} onOk={confirmRenew} okText={`Renew for ${years} ${years === 1 ? 'year' : 'years'}`} confirmLoading={busy} destroyOnHidden>
      <p style={{ marginTop: 0 }}>Keeps the cloud services running on this activated account. The years start after its current renewal ends, or today if it has ended. No payment is taken.</p>
      <Select value={years} onChange={setYears} aria-label="Years of renewal" style={{ width: 160 }} options={Array.from({ length: 10 }, (_, index) => ({ value: index + 1, label: `${index + 1} ${index === 0 ? 'year' : 'years'}` }))}/>
    </Modal>);
    return { activate, renew, dialog };
}
